'use client';

import { useEffect, useId, useState } from 'react';

export type CollapsiblePropsBase = {
  defaultExpanded?: boolean;
  onCollapseChange?: (isOpen: boolean) => void;
  onCollapse?: () => void;
  onExpand?: () => void;
  forceMount?: boolean;
};

export type CollapsibleTriggerProps = {
  'data-state': 'open' | 'closed';
  'aria-controls': string;
  'aria-expanded': boolean;
  onClick: () => void;
};

export type CollapsibleTargetProps = {
  id: string;
  'data-state': 'open' | 'closed';
  className?: string;
  collapsed: 'true' | 'false';
  hide?: boolean;
};

export type UseCollapsibleReturnBase = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type UseCollapsibleReturnEnabled = UseCollapsibleReturnBase & {
  triggerProps: CollapsibleTriggerProps;
  targetProps: CollapsibleTargetProps;
};

export type UseCollapsibleReturnDisabled = UseCollapsibleReturnBase & {
  triggerProps: null;
  targetProps: null;
};

// Overload 1: No props, assume enabled.
export default function useCollapsible(
  props?: CollapsiblePropsBase & { enabled?: true },
): UseCollapsibleReturnEnabled;

// Overload 2: not enabled.
export default function useCollapsible(
  props: CollapsiblePropsBase & { enabled: false },
): UseCollapsibleReturnDisabled;

// Overload 3: dynamic.
export default function useCollapsible(
  props: CollapsiblePropsBase & { enabled: boolean },
): UseCollapsibleReturnEnabled | UseCollapsibleReturnDisabled;

/**
 * Hook for collapsible functionality.
 */
export default function useCollapsible({
  defaultExpanded = false,
  enabled = true,
  forceMount = false,
  onCollapse,
  onCollapseChange,
  onExpand,
}: CollapsiblePropsBase & { enabled?: boolean } = {}):
  | UseCollapsibleReturnEnabled
  | UseCollapsibleReturnDisabled {
  const generatedId = useId();
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    onCollapseChange?.(isOpen);

    if (isOpen) {
      onExpand?.();
    } else {
      onCollapse?.();
    }
  }, [enabled, isOpen]);

  const stateAttr: 'open' | 'closed' = isOpen ? 'open' : 'closed';

  if (!enabled) {
    return {
      triggerProps: null,
      targetProps: null,
      isOpen,
      setIsOpen,
    };
  }

  return {
    triggerProps: {
      'data-state': stateAttr,
      'aria-controls': generatedId,
      'aria-expanded': isOpen,
      onClick: () => setIsOpen((prev) => !prev),
    },
    targetProps: {
      id: generatedId,
      'data-state': stateAttr,
      className: !isOpen && forceMount ? 'hidden' : '',
      collapsed: (!isOpen).toString() as 'true' | 'false',
      hide: !isOpen && !forceMount ? true : undefined,
    },
    isOpen,
    setIsOpen,
  };
}
